import {Grid, Paper, TextField, Button} from "@mui/material"
import React, {useEffect, useState} from "react"
import LayoutPage from "src/components/page-layout/layout-page"
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import PromptService from "src/services/prompt-service"
import ModalDialogPage from "src/components/modal-dialog"
import FabButton from "src/components/fab-button"
import MoreVertButton from "src/components/more-vert-button"
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import ConfirmDialog from "src/components/confirm-dialog"
import {DataGrid} from "@mui/x-data-grid"
import {localeBRDataGridText} from "src/utils/data-grid-local-text"
import AlertMessage from "src/components/alert-message"

// const TIPO_DOCUMENTOS = [
//     "Termo de Securitização",
//     "Escritura de Debênture",
//     "Outras Escrituras",
//     "Tela CETIP Corporate",
//     "Tela Cetip IF",
//     "Fluxo de Eventos",
//     "CVM Fatos Relevantes"
// ]

const DEFAULT_PROMPT = {
    ID: null,
    TipoDocumento: "",
    Prompt: ""
}

const PromptPage = () => {
    const promptService = new PromptService()
    const [openNewPrompt, setOpenNewPrompt] = useState(false)
    const [loading, setLoading] = useState(false)
    const [rowState, setRowState] = useState([])
    const [selectedPrompt, setSelectedPrompt] = useState(DEFAULT_PROMPT)
    const [dialogDeletePrompt, setDialogDeletePrompt] = useState({
        open: false,
        title: "",
        prompt: null
    })
    const [alertMessageState, setAlertMessageState] = useState({
        open: false,
        message: "",
        type: ""
    })

    const actionButton = {
        icon: <AddRoundedIcon/>,
        onClick: () => {
            setOpenNewPrompt(true)
        },
        color: "primary",
    }

    useEffect(() => {
        loadPrompts()
    }, [])

    const openAlert = (message, type) => {
        setAlertMessageState({
            ...alertMessageState,
            open: true,
            message: message,
            type: type
        })
    }

    const closeAlert = () => {
        setAlertMessageState({...alertMessageState, open: false})
    }

    const loadPrompts = () => {
        setLoading(true)
        promptService.getPrompts().then((response) => {
            const {body} = response
            const {results} = body
            const rows = results.map((obj, index) => ({...obj, id: index}))
            setRowState(rows)
            setLoading(false)
        }).catch(() => {
            setLoading(false)
        })
    }

    const reloadPrompts = () => {
        setRowState([])
        setSelectedPrompt(DEFAULT_PROMPT)
        loadPrompts(0)
    }

    const openEditPrompt = (prompt) => {
        const {ID, TipoDocumento, Prompt} = prompt
        setSelectedPrompt({
            ID: ID,
            TipoDocumento: TipoDocumento,
            Prompt: Prompt,
        })
        setOpenNewPrompt(true)
    }

    const changeValue = (event, field) => {
        const {target: {value}} = event
        setSelectedPrompt({...selectedPrompt, [field]: value})
    }

    const criaNovoPrompt = () => {
        setLoading(true)
        const {TipoDocumento, Prompt} = selectedPrompt

        promptService.createPrompt(TipoDocumento, Prompt)
            .then(() => {
                openAlert("Prompt criado com sucesso!", "success")
                reloadPrompts()
                setOpenNewPrompt(false)
            })
            .catch(() => {
                openAlert("Erro ao criar prompt. Tente novamente!", "error")
            }).finally(() => setLoading(false))
    }

    const atualizaPrompt = () => {
        const {ID, TipoDocumento, Prompt} = selectedPrompt
        setLoading(true)
        promptService.updatePrompt(ID, TipoDocumento, Prompt)
            .then(() => {
                openAlert("Prompt atualizado com sucesso!", "success")
                reloadPrompts()
                setOpenNewPrompt(false)
            })
            .catch(() => {
                openAlert("Erro ao atualizar prompt. Tente novamente!", "error")
            }).finally(() => setLoading(false))
    }

    const openDialogDeletePrompt = (prompt) => {
        const {ID, TipoDocumento} = prompt
        setDialogDeletePrompt({
            open: true,
            title: `Deseja realmente excluir o prompt ${TipoDocumento}?`,
            id: ID,
            promptName: TipoDocumento
        })
    }

    const deletePrompt = () => {
        const {id, promptName} = dialogDeletePrompt
        setLoading(true)
        promptService.deletePrompt(id).then(() => {
            setLoading(false)
            openAlert(`Prompt ${promptName} removido!`, "warning")
            reloadPrompts()
        }).catch(() => {
            openAlert(`Erro ao tentar remover o prompt ${promptName}. Tente novamente!`, "error")
            setLoading(false)
        })
    }

    const options = [
        {
            icon: <EditRoundedIcon/>,
            text: "Editar",
            function: openEditPrompt
        },
        {
            icon: <DeleteRoundedIcon color="error"/>,
            function: openDialogDeletePrompt,
            text: "Excluir"
        },
    ]

    const columns = [
        {
            field: 'ID',
            headerName: '#',
            maxWidth: 80
        },
        {
            field: 'TipoDocumento',
            headerName: 'Tipo Documento',
            flex: 1,
            minWidth: 200,
        },
        {
            field: 'Prompt',
            headerName: 'Prompt',
            flex: 2,
            minWidth: 600,
            renderCell: (params) => {
                const texto = params.value || ""
                const limiteCaracteresVisiveis = 300
                const textoLimitado = texto.length > limiteCaracteresVisiveis ? texto.substring(0, limiteCaracteresVisiveis) + "..." : texto
                return (
                    <span
                        title={texto} // mostra o texto completo ao passar o mouse
                        style={{
                            whiteSpace: "pre-wrap",    // permite quebra de linha
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,         // limita o prompt a 2 linhas visíveis
                            WebkitBoxOrient: "vertical",
                            lineHeight: "20px",
                            maxHeight: "40px",         // 2 linhas * lineHeight
                        }}
                    >
                        {textoLimitado}
                    </span>
                )
            }
        },
        {
            field: 'Edição/Deleção',
            headerName: 'Opções',
            flex: true,
            renderCell: (params) => <MoreVertButton id={params.row.prompt} options={options} item={params.row}/>,
            minWidth: 100,
        },
    ]

    return (
        <LayoutPage title="Prompts" loading={loading}>
            <AlertMessage open={alertMessageState.open} message={alertMessageState.message}
                          type={alertMessageState.type} close={closeAlert}/>
            <div>
                <Grid container direction="row" justifyContent="center" spacing={2}>
                    <Grid item xl={12} md={12} xs={12}>
                        <Paper sx={{mt: { md: 7, xs: 0 }}}>
                            <DataGrid
                                autoHeight
                                getRowHeight={() => 'auto'}
                                rows={rowState}
                                columns={columns}
                                pageSize={25}
                                disableRowSelectionOnClick
                                sortingOrder={['asc', 'desc']}
                                localeText={localeBRDataGridText}
                            />
                        </Paper>
                    </Grid>
                </Grid>

                <ModalDialogPage
                    open={openNewPrompt}
                    title={selectedPrompt == null ? "Novo Prompt" : "Editar Prompt"}
                    close={() => {
                        setOpenNewPrompt(false)
                        setSelectedPrompt(DEFAULT_PROMPT)
                    }}
                    button={<FabButton action={actionButton}/>}
                >
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                name="Tipo Documento"
                                label="TipoDocumento"
                                variant="outlined"
                                fullWidth
                                onChange={(event) => changeValue(event, "TipoDocumento")}
                                value={selectedPrompt.TipoDocumento}
                                InputLabelProps={{shrink: true}}
                            />
                            {/*<FormControl fullWidth variant="outlined">*/}
                            {/*    <InputLabel id="tipo-documento-label">Tipo de Documento</InputLabel>*/}
                            {/*    <Select*/}
                            {/*        labelId="tipo-documento-label"*/}
                            {/*        label="Tipo de Documento"*/}
                            {/*        onChange={(event) => changeValue(event, "TipoDocumento")}*/}
                            {/*        name="TipoDocumento"*/}
                            {/*        fullWidth*/}
                            {/*        value={selectedPrompt?.TipoDocumento ?? ""}*/}
                            {/*    >*/}
                            {/*        {TIPO_DOCUMENTOS.map((item, index) => (*/}
                            {/*            <MenuItem key={`${item}-${index}-select`} value={item}>*/}
                            {/*                {item}*/}
                            {/*            </MenuItem>*/}
                            {/*        ))}*/}
                            {/*    </Select>*/}
                            {/*</FormControl>*/}
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="Prompt"
                                label="Prompt"
                                variant="outlined"
                                fullWidth
                                multiline
                                onChange={(event) => changeValue(event, "Prompt")}
                                minRows={20}
                                maxRows={20}
                                value={selectedPrompt.Prompt}
                                InputLabelProps={{shrink: true}}
                                placeholder="Descreva o prompt"
                            />
                        </Grid>
                        <Grid item xs={12} display="flex" justifyContent="flex-end">
                            {selectedPrompt.ID !== null ?
                                <Button type="submit" variant="contained" onClick={atualizaPrompt}>
                                    Atualizar
                                </Button>
                                :
                                <Button type="submit" variant="contained" onClick={criaNovoPrompt}>
                                    Salvar
                                </Button>}
                        </Grid>
                    </Grid>
                </ModalDialogPage>
                <ConfirmDialog
                    open={dialogDeletePrompt.open}
                    title={dialogDeletePrompt.title}
                    onConfirm={() => deletePrompt()}
                    onClose={() => setDialogDeletePrompt({title: "", open: false, prompt: null})}
                >
                    <span>Essa ação não pode ser desfeita.</span>
                </ConfirmDialog>
            </div>
        </LayoutPage>
    )

}
export default PromptPage